import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Heart, MapPin, Truck, Sparkles } from "lucide-react";
import { IMAGES } from "@/data/site";

const line = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: { delay: 0.2 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
    }),
};

const fade = {
    hidden: { opacity: 0, y: 24 },
    show: (i) => ({
        opacity: 1,
        y: 0,
        transition: { delay: 0.7 + i * 0.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    }),
};

const HIGHLIGHTS = [
    { icon: Heart, text: "Family Owned & Operated" },
    { icon: MapPin, text: "Shelton · Olympia · Lacey" },
    { icon: Truck, text: "Pickup & Local Delivery" },
];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yImage = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const yCard = useTransform(scrollYProgress, [0, 1], [0, -70]);
    const rotate = useTransform(scrollYProgress, [0, 1], [0, 6]);

    return (
        <section id="home" ref={ref} data-testid="hero-section" className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
            <div className="pointer-events-none absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-[#F8D7E3]/60 blur-3xl" />
            <div className="pointer-events-none absolute top-64 -left-40 h-[380px] w-[380px] rounded-full bg-[#FAF0D7]/70 blur-3xl" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    <div className="lg:col-span-6">
                        <motion.div variants={fade} custom={0} initial="hidden" animate="show" className="mb-6">
                            <span
                                data-testid="hero-eyebrow"
                                className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#FAF0D7]/60 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]"
                            >
                                <Sparkles className="h-3.5 w-3.5" />
                                Custom Cakes · Cupcakes · Cookies · More
                            </span>
                        </motion.div>

                        <h1 data-testid="hero-headline" className="font-display font-bold tracking-tight text-[#3D2318] text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">
                            <span className="block overflow-hidden pb-1">
                                <motion.span variants={line} custom={0} initial="hidden" animate="show" className="block">
                                    Homemade Goodness
                                </motion.span>
                            </span>
                            <span className="block overflow-hidden pb-2">
                                <motion.span variants={line} custom={1} initial="hidden" animate="show" className="block">
                                    for{" "}
                                    <span className="font-script font-medium text-[#C95B7D] text-5xl sm:text-6xl lg:text-7xl align-middle">
                                        Every Occasion
                                    </span>
                                </motion.span>
                            </span>
                        </h1>

                        <motion.p
                            variants={fade}
                            custom={1}
                            initial="hidden"
                            animate="show"
                            data-testid="hero-subheadline"
                            className="mt-6 max-w-xl text-base sm:text-lg text-[#6B4A3E] leading-relaxed font-light"
                        >
                            Whether you're celebrating a birthday, holiday, wedding, baby shower — or simply craving
                            something sweet — everything here is homemade with care, creativity, and a whole lot of love.
                        </motion.p>

                        <motion.div variants={fade} custom={2} initial="hidden" animate="show" className="mt-9 flex flex-wrap items-center gap-4">
                            <a
                                href="#order"
                                data-testid="hero-order-button"
                                className="group inline-flex items-center gap-2 rounded-full bg-[#C95B7D] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(201,91,125,0.4)] hover:bg-[#b44a6b] hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
                            >
                                Request an Order
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </a>
                            <a
                                href="#menu"
                                data-testid="hero-menu-button"
                                className="inline-flex items-center gap-2 rounded-full border border-[#3D2318]/15 bg-white/70 px-7 py-3.5 text-sm font-semibold text-[#3D2318] hover:border-[#C59B27]/60 hover:bg-[#FAF0D7]/70 hover:-translate-y-0.5 transition-[transform,background-color,border-color] duration-300"
                            >
                                View Our Menu
                            </a>
                        </motion.div>

                        <motion.ul variants={fade} custom={3} initial="hidden" animate="show" className="mt-10 flex flex-wrap gap-x-6 gap-y-2" data-testid="hero-highlights">
                            {HIGHLIGHTS.map(({ icon: Icon, text }) => (
                                <li key={text} className="flex items-center gap-2 text-xs font-medium text-[#8C6A5D]">
                                    <Icon className="h-4 w-4 text-[#C59B27]" />
                                    {text}
                                </li>
                            ))}
                        </motion.ul>
                    </div>

                    <div className="lg:col-span-6 relative">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.35, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            className="relative mx-auto max-w-md lg:max-w-none"
                        >
                            <motion.div style={{ y: yImage, rotate }} className="relative">
                                <div className="overflow-hidden rounded-t-[999px] rounded-b-[2rem] border-[10px] border-white shadow-[0_30px_80px_rgba(61,35,24,0.25)]">
                                    <img
                                        src={IMAGES.realMarble}
                                        alt="Blue marble butterfly cake made by Annabell"
                                        data-testid="hero-image"
                                        className="h-[420px] sm:h-[520px] w-full object-cover"
                                    />
                                </div>
                                <motion.img
                                    src="/annabells-logo.png"
                                    alt="Annabells Cupcakes and More badge"
                                    data-testid="hero-logo-badge"
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                                    className="absolute -top-6 -right-4 sm:-right-8 h-24 w-24 sm:h-32 sm:w-32 rounded-full object-cover ring-4 ring-white shadow-xl"
                                />
                            </motion.div>

                            <motion.div
                                style={{ y: yCard }}
                                className="absolute -bottom-8 -left-4 sm:-left-10 w-40 sm:w-52 overflow-hidden rounded-2xl border-4 border-white shadow-[0_20px_50px_rgba(61,35,24,0.25)]"
                            >
                                <img src={IMAGES.realStrawberries} alt="Chocolate-covered strawberries made by Annabell" className="h-36 sm:h-44 w-full object-cover" />
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
